// AST Graph builder (ARCHITECTURE.md §6.7, step 4).
//
// `buildAstGraphFromResults` merges language-independent `FileAnalysisResult`
// IRs into a single, language-erased `AstGraph`. This is a pure function: it
// performs no IO and no store interaction (orchestration lives in ./index.ts).
//
// Each result becomes one `file` AstNode plus one `symbol` AstNode per analyzed
// symbol that carries a `symbolId`, with a `contains` edge from the file to each
// symbol. Edges from every result (imports, calls, type_reference, …) are then
// concatenated and deduplicated by (from, to, kind) — the first occurrence wins,
// so its evidence is retained.

import type { FileAnalysisResult } from "../analyzer/types.js";
import type { GraphEdge } from "../types.js";
import type { AstGraph, AstNode } from "./types.js";

/** Stable dedup key for an edge: (from, to, kind). */
function edgeKey(edge: GraphEdge): string {
  return `${edge.from} ${edge.to} ${edge.kind}`;
}

/**
 * Build an `AstGraph` from analyzed files. Pure — no IO, no store.
 *
 * @param results    One IR per analyzed file.
 * @param fileHashes path → SHA-256 content hash, recorded on the graph for the
 *                   incremental cache. Copied defensively.
 */
export function buildAstGraphFromResults(
  results: FileAnalysisResult[],
  fileHashes: Map<string, string>,
): AstGraph {
  const nodes = new Map<string, AstNode>();
  const edges: GraphEdge[] = [];
  const seen = new Set<string>();

  for (const result of results) {
    // One file node per result; language identity is erased from the node
    // itself (kept only as informational metadata, §4.4).
    nodes.set(result.filePath, {
      id: result.filePath,
      kind: "file",
      filePath: result.filePath,
      metadata: {
        language: result.language,
        parserBackend: result.metadata.parserBackend,
        lineCount: result.metadata.lineCount,
      },
      symbols: result.symbols,
    });

    // One `symbol` node per analyzed symbol, with a file → symbol `contains`
    // edge. Symbols without a symbolId (e.g. analyzers not yet emitting it) are
    // skipped — they remain available as `file` node metadata.
    for (const sym of result.symbols) {
      if (!sym.symbolId) continue;
      nodes.set(sym.symbolId, {
        id: sym.symbolId,
        kind: "symbol",
        filePath: result.filePath,
        metadata: {
          symbolKind: sym.kind,
          exported: sym.exported,
          ...(sym.parentSymbolId
            ? { parentSymbolId: sym.parentSymbolId }
            : {}),
        },
        symbolInfo: sym,
      });

      const containsKey = `${result.filePath} ${sym.symbolId} contains`;
      if (!seen.has(containsKey)) {
        seen.add(containsKey);
        edges.push({
          from: result.filePath,
          to: sym.symbolId,
          kind: "contains",
          classification: "deterministic",
          evidence: [
            {
              type: "static_import",
              detail: `defines ${sym.name}`,
              line: sym.line,
              confidence: 1,
            },
          ],
        });
      }
    }

    for (const edge of result.edges) {
      const key = edgeKey(edge);
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push(edge);
    }
  }

  return {
    nodes,
    edges,
    fileHashes: new Map(fileHashes),
  };
}
